import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as ApplicationsService from '../services/ApplicationsService';
import { Application } from '../types';
import { notifyError, notifySuccess } from '../../../components/PlatformNotification';

// Query keys
export const applicationsKeys = {
    all: ['applications'] as const,
    lists: () => [...applicationsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...applicationsKeys.lists(), { filters }] as const,
    details: () => [...applicationsKeys.all, 'detail'] as const,
    detail: (id: string) => [...applicationsKeys.details(), id] as const,
};

const PAGE_SIZE = 50;

// FNP-913: TOKEN PAGINATION.
//
// This was `useQuery` calling a service that returned `.items` and threw
// `nextToken` away — so the list was never paginated at all. It read as
// compliant (query hook ✓, structured keys ✓, invalidation ✓) while fetching
// one unbounded page, which is exactly the failure mode §9 of the module
// checklist now calls out explicitly.
//
// getNextPageParam MUST return `undefined` (not null, not the same token) when
// there are no more pages, or react-query refetches forever.
export const useApplicationsInfiniteQuery = (filters: Record<string, string> = {}) => {
    return useInfiniteQuery({
        queryKey: applicationsKeys.list(filters),
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            ApplicationsService.fetchRecords(filters, PAGE_SIZE, pageParam),
        getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
        initialPageParam: null as string | null,
    });
};

export const useApplicationQuery = (id: string) => {
    return useQuery({
        queryKey: applicationsKeys.detail(id),
        queryFn: () => ApplicationsService.fetch(id),
        enabled: !!id,
    });
};

export const useCreateApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ApplicationsService.add,
        onSuccess: () => {
            notifySuccess('Application created');
            queryClient.invalidateQueries({ queryKey: applicationsKeys.lists() });
        },
        onError: notifyError,
    });
};

export const useUpdateApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, record }: { id: string; record: Application }) =>
            ApplicationsService.update(id, record),
        onSuccess: (_, { id }) => {
            notifySuccess('Application updated');
            queryClient.invalidateQueries({ queryKey: applicationsKeys.lists() });
            queryClient.invalidateQueries({ queryKey: applicationsKeys.detail(id) });
        },
        onError: notifyError,
    });
};

export const useDeleteApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ApplicationsService.deleteRecord,
        onSuccess: () => {
            notifySuccess('Application deleted');
            queryClient.invalidateQueries({ queryKey: applicationsKeys.all });
        },
        onError: notifyError,
    });
};

// FNP-998: start an infrastructure teardown.
//
// ⚠️ Distinct from useDeleteApplicationMutation. That deletes the RECORD and
// touches no AWS resources; this destroys the RESOURCES and keeps the record,
// marked DECOMMISSIONED.
//
// The mutation returns as soon as the workflow starts — it does not wait for
// the teardown, which takes 15-25 minutes because a CloudFront distribution
// must be disabled and propagated before it can be deleted. So the success
// notification says "started", not "decommissioned": telling the admin it was
// decommissioned while the site is still serving is the exact confusion this
// ticket exists to remove.
//
// The record is invalidated so the status badge picks up DELETING immediately.
// FNP-1013: start building an application's infrastructure.
//
// ⚠️ The success notification says "started", not "provisioned", for the same
// reason the teardown below says "started": the mutation returns as soon as the
// workflows-api Execution is running, and the build takes many minutes. Telling
// the admin it is provisioned while nothing exists yet is the confusion the
// wording exists to avoid.
//
// The record is invalidated so the status badge picks up CREATING immediately,
// and so `provisioningExecutionId` is present for the progress section to read.
export const useProvisionApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (
            {id, playbookId, stage, subdomain, deploymentAccountId, baseName}: {
                id: string;
                playbookId: string;
                stage: string;
                subdomain?: string;
                deploymentAccountId?: string;
                baseName?: string;
            }) =>
            ApplicationsService.provision(
                id, playbookId, stage, subdomain, deploymentAccountId, baseName,
            ),
        onSuccess: () => {
            notifySuccess(
                'Provisioning started. This takes several minutes; the application '
                + 'shows CREATING until it completes.',
            );
            queryClient.invalidateQueries({queryKey: applicationsKeys.all});
        },
        onError: notifyError,
    });
};

export const useDeprovisionApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({id, stage}: {id: string; stage: string}) =>
            ApplicationsService.deprovision(id, stage),
        onSuccess: () => {
            // FNP-1034: DO NOT PROMISE A DURATION HERE. This fires when the
            // execution STARTS, and an execution can fail in seconds — one
            // died 16 seconds in while this toast told the owner to wait
            // 15-25 minutes, with the app already serving 403 because its
            // bucket had been emptied. A number the mutation cannot know is
            // worse than no number: it tells the admin to stop looking.
            //
            // The state machine now records a terminal FAILED with a reason,
            // so the progress panel is the honest place to learn the outcome.
            // Point at it instead of predicting one.
            notifySuccess(
                'Decommissioning started. Follow its progress on this page — '
                + 'the application shows DELETING while it runs.',
            );
            queryClient.invalidateQueries({queryKey: applicationsKeys.all});
        },
        onError: notifyError,
    });
};

// FNP-1007: mark an orphaned application decommissioned.
//
// ⚠️ A REFUSAL IS A SUCCESSFUL RESPONSE, NOT AN ERROR. The mutation resolves
// with `marked: false` and a structured reason when the sweep finds resources
// or cannot complete — it does not throw. So `onSuccess` must NOT announce
// success unconditionally; it checks `marked` first. Notifying "decommissioned"
// on a refusal would tell the admin the opposite of what happened, and the
// refusals are the whole reason this result type is structured.
//
// The caller renders `found` / `incompleteChecks` itself — those need distinct
// treatment in the modal and cannot survive being flattened into a toast.
//
// On a successful mark the record is renamed, so BOTH the lists and the detail
// are invalidated: the list must show the new name, and the open detail page
// must pick up DECOMMISSIONED.
export const useMarkApplicationDecommissionedMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({id}: {id: string}) =>
            ApplicationsService.markDecommissioned(id),
        onSuccess: (result) => {
            if (!result?.marked) {
                // Refused. The modal renders the structured detail; a toast
                // here would either duplicate it or, worse, contradict it.
                return;
            }
            notifySuccess(
                `Marked as decommissioned. The record is now "${result.name}" `
                + 'and no infrastructure was changed.',
            );
            queryClient.invalidateQueries({queryKey: applicationsKeys.all});
        },
        onError: notifyError,
    });
};

// Global filter state (shared across components)
//
// FNP-1546: NO DEFAULT FILTER. This used to start at
// `{applicationStatus: 'ACTIVE'}`, a filter the operator never chose.
//
// ⚠️ AN ABSENT STATUS DOES NOT MATCH 'ACTIVE'. A freshly created record has no
// `applicationStatus` attribute at all, so the default hid every application
// from the moment it was created — the create succeeded, the toast said so,
// and the row was not on the list. Creating it again then failed the
// uniqueness check, which is an unintelligible error about a record the screen
// says does not exist. It also put two different numbers for one question on
// one screen: the dashboard card counted unfiltered (11), the list filtered (9).
//
// The operator opts IN to a status filter; the control is right there and
// `handleClearFilters` has always been able to reach this empty state.
let globalFilters: Record<string, string> = {};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useApplicationsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};
